import { useQuery } from '@tanstack/react-query';

import { catalogApi, companyApi } from '@/api';
import { Alert, ErrorState, Icon, LinkButton, LoadingState, PageHeader } from '@/components/ui';
import { BrandingCompletion } from '@/features/branding/BrandingCompletion';
import { BrandingForm } from '@/features/branding/BrandingForm';
import { BRANDING_KEY } from '@/features/branding/cache';
import { CoverEditor } from '@/features/branding/CoverEditor';
import { GalleryEditor } from '@/features/branding/GalleryEditor';
import { useAuth } from '@/hooks/useAuth';
import { errorMessage } from '@/hooks/useApiError';
import { usePermissions } from '@/hooks/usePermissions';
import { paths } from '@/routes/paths';
import { PERMISSIONS } from '@/types/roles';

/**
 * The employer page students read before applying: what the company says
 * about itself. The trust signals beside it on the public page are counted by
 * the platform and are not edited here.
 *
 * Company admins and HR edit it; every other workspace role gets the same
 * screen read-only, so they can still see what is published.
 */
export function CompanyBrandingPage() {
  const { user } = useAuth();
  const { can } = usePermissions();
  const readOnly = !can(PERMISSIONS.COMPANY_BRANDING_UPDATE);
  const slug = user?.company?.company_slug;

  const {
    data: branding,
    isPending,
    isError,
    error,
    refetch,
  } = useQuery({ queryKey: BRANDING_KEY, queryFn: companyApi.getBranding });

  const { data: perks } = useQuery({
    queryKey: ['company-perks'],
    queryFn: catalogApi.listCompanyPerks,
    staleTime: Infinity,
  });

  if (isPending) return <LoadingState label="Loading your employer page…" />;
  if (isError) {
    return <ErrorState message={errorMessage(error)} onRetry={() => void refetch()} />;
  }

  return (
    <div>
      <PageHeader
        eyebrow="Organisation"
        title="Employer brand"
        description="Show students what it is like to intern with you. Everything here appears on your public company page."
        action={
          slug && (
            <LinkButton
              to={paths.companyPublic(slug)}
              variant="secondary"
              target="_blank"
              rel="noopener"
            >
              <Icon name="external" size={16} />
              View public page
            </LinkButton>
          )
        }
      />

      {readOnly && (
        <Alert tone="info" className="mb-6" title="View only">
          Company admins and HR can edit the employer page. Ask one of them to make a change.
        </Alert>
      )}

      <div className="grid items-start gap-6 lg:grid-cols-[1fr_340px]">
        <div className="min-w-0 space-y-6">
          <CoverEditor branding={branding} readOnly={readOnly} />
          <BrandingForm
            branding={branding}
            perkCatalogue={perks ?? branding.perks}
            companyName={user?.company?.company_name}
            readOnly={readOnly}
          />
          <GalleryEditor branding={branding} readOnly={readOnly} />
        </div>

        <div className="space-y-6 lg:sticky lg:top-6">
          <BrandingCompletion completion={branding.completion} />
        </div>
      </div>
    </div>
  );
}
